import CardCommon from '@op/components/CardCommon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useChannelShow,
  useEmptyText,
  useShortUrlFilterValue,
} from '../../CustomAISummary/hooks';
import { PromptConfirmItemType } from '@op/components/GPT/types';
import { useFilterCondConf } from '@op/hooks/useFilterCondConf';
import { updateURL, useCustomInsightReportList } from '../../hooks';
import { CustomGptBody, ReportGptProps } from '../../components';
import { copyIcon, editIcon, viewDetailsIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import SvgIcon from '@home/components/SvgIcon';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import SummaryModal from '../../CustomAISummary/component/SummaryModal';
import { useLocation } from 'umi';
import { Button } from 'tea-component';
import useReportLanguage from '@op/pages/report/hooks/useReportLanguage';
import { DBO_U } from '@op/utils/format';
import ReportFilter from '../ReportFilter';

export default function CustomGpt(props: ReportGptProps) {
  const location = useLocation();
  const {
    language: defaultLanguage,
    report_start_time,
    report_end_time,
  } = DBO_U.queryObj(location?.search);

  const { task, gameInfo, requestData, pageName, wholeGameInfo } = props;
  const { formatMessage } = useLanguage();
  const {
    loading: pageLoading,
    isEmpty,
    language,
    setLanguage,
    gptLanguage,
    ...filterProps
  } = useCustomInsightReportList({
    gameInfo,
    task,
    defaultLanguage,
    defaultValue: {
      start_time: report_start_time ? decodeURIComponent(report_start_time) : null,
      end_time: report_end_time ? decodeURIComponent(report_end_time) : null,
    },
  });

  const { loading, filter } = useShortUrlFilterValue({
    id: task?.short_url,
    alwaysLoading: true,
  });
  const [promptInfo, setPromptInfo] = useState<PromptConfirmItemType>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalProps, setModalProps] = useState({});
  const { commentList, socialList, backCondOriginData } = useChannelShow();
  const [promptFilter, setPromptFilter] = useState({
    start_time: filter?.start_time || '',
    end_time: filter?.end_time || '',
  });

  const [gptStatus, setGptStatus] = useState<{ status: string }>({
    status: 'loading',
  });
  const { channel } = useFilterCondConf({
    gameInfo,
    needRender: true,
    requestList: [{ key: 'channel', defaultValue: {} }],
    needRequest: true,
  });

  useEffect(() => {
    backCondOriginData({ channel });
  }, [channel]);

  const params = useMemo(
    () => ({ ...filter, ...gameInfo, ...promptFilter }),
    [filter, gameInfo, promptFilter],
  );

  const toggleModal = (type: string, props: any = {}) => {
    setModalType(type);
    setModalVisible(!modalVisible);
    setModalProps(props);
  };

  // 权限控制
  const { Update_Repeat_Report } = useGetSingePageSourceRule({
    keys: ['Update_Repeat_Report'],
  });

  const { emptyProps, getGPTEmptyProps } = useEmptyText({ promptInfo: task });

  const dropdownRef = useRef<{
    handleRecentReport: () => void;
    validReport: () => boolean;
  }>(null);
  const handleRecentReport = () => {
    dropdownRef?.current?.handleRecentReport();
  };

  const { options, loading: optionsLoading } = useReportLanguage({
    gameInfo,
    setLanguage,
  });

  return (
    <>
      <CardCommon
        loading={pageLoading || loading || optionsLoading}
        empty={isEmpty}
        emptyHeight={600}
        loadingHeight={600}
        contentCard
        {...emptyProps}
      >
        <ReportFilter
          pageName={pageName}
          confirm={(value) => {
            setPromptInfo({
              taskId: value?.task_id,
              shortId: value?.short_url,
              gptOptions: value?.task_type,
              modelType: value?.model_type,
            });
            setPromptFilter({
              start_time: value?.start_time || '',
              end_time: value?.end_time || '',
            });
            setGptStatus({ status: value?.status || 'init' });
            updateURL({
              updateParams: {
                report_start_time: value?.start_time || '',
                report_end_time: value?.end_time || '',
              },
            });
          }}
          hasLanguage={promptInfo?.gptOptions === 'advanced'}
          language={language}
          setLanguage={(value) => {
            setLanguage?.(value);
            updateURL({
              updateParams: { language: value },
            });
          }}
          dropdownRef={dropdownRef}
          options={options}
          {...filterProps}
        ></ReportFilter>
        <CardCommon
          empty={gptStatus?.status !== 'success'}
          contentCard={gptStatus?.status === 'success'}
          emptyHeight={500}
          bodyStyle={{ marginTop: 20 }}
          {...getGPTEmptyProps(gptStatus)}
          emptyDescElement={
            dropdownRef?.current?.validReport ? (
              <Button
                type="link"
                style={{ paddingLeft: 5 }}
                onClick={() => handleRecentReport?.()}
              >
                {formatMessage({ id: 'Click to view recent report' })}
              </Button>
            ) : null
          }
        >
          <CustomGptBody
            {...props}
            language={gptLanguage}
            filter={params}
            loading={loading}
            commentList={commentList}
            socialList={socialList}
            promptInfo={promptInfo}
            hasStopBtn={false}
            oriFeedsState={{ total: 0 }}
            openCardLoading
            isSubscribe={true}
            operations={
              !!!Update_Repeat_Report
                ? [viewDetailsIcon, editIcon, copyIcon]
                : [viewDetailsIcon, copyIcon]
            }
            handleExtraOperations={({ symbol }: { symbol: string }) => {
              if (symbol === 'view_details') {
                buriedBtnPoint('RepeatDetailSetting', pageName);
                toggleModal('detail', { records: task });
                return true;
              }
              if (symbol === 'edit') {
                buriedBtnPoint('EditRepeatSettings', pageName);
                toggleModal('edit', { records: task });
                return true;
              }
              return false;
            }}
            extraFloatIcon={
              <>
                <div className="dbo-button-float-details">
                  <SvgIcon
                    onClick={() => {
                      toggleModal('detail', { records: task });
                    }}
                    type="view-details-icon"
                  />
                </div>
                {!!!Update_Repeat_Report && (
                  <div className="dbo-button-float-edit">
                    <SvgIcon
                      onClick={() => {
                        toggleModal('edit', { records: task });
                      }}
                      type="edit-icon"
                    />
                  </div>
                )}
              </>
            }
          ></CustomGptBody>
        </CardCommon>
      </CardCommon>
      {modalVisible && (
        <SummaryModal
          visible={modalVisible}
          modalType={modalType}
          toggleVisible={toggleModal}
          formatMessage={formatMessage}
          gameInfo={gameInfo}
          request={requestData}
          pageName={pageName}
          wholeGameInfo={wholeGameInfo}
          {...modalProps}
        />
      )}
    </>
  );
}
